import React, { useEffect, useMemo, useState } from 'react'
import SearchBar from 'pages/pga-coach/students/search-bar'
import { makeStyles } from '@material-ui/styles'
import { useIsTablet } from 'lib/hooks'
import StudentItemView from './student-item-view'
import {
  sortByAgeGroup,
  sortByCoach,
  sortByFullName,
  sortByUpcoming,
} from 'lib/student-list/utils'
import { parseStudents } from '../utils'
import EnhancedTable from 'components/tables/enhanced-table'
import StudentsTablePagination from 'pages/pga-coach/students/students-table-pagination'
import PermContactCalendarOutlinedIcon from 'components/material-symbol/icons/PermContactCalendar'
import SectionEmptyState from 'components/section-empty-state'
import ArrowDropDownIcon from 'components/material-symbol/icons/ArrowDropDown'
import SwapVertIcon from 'components/material-symbol/icons/SwapVert'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Popover,
  Radio,
  RadioGroup,
  TableContainer,
} from '@material-ui/core'

const AGE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Ages' },
  { value: 'adult', label: 'Adult' },
  { value: 'junior', label: 'Junior' },
]

export const useStyles = makeStyles(theme => ({
  containerStyles: {
    boxSizing: 'border-box',
    padding: theme.spacing(3, 4.5, 2),
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '60px',
      padding: theme.spacing(3, 1, 2),
    },
  },
  tableShell: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
  },
  filterButton: {
    height: 40,
    borderRadius: 8,
    fontWeight: 500,
    flexShrink: 0,
    borderColor: '#C4C7C9',
    color: '#172024',
    '& .MuiButton-endIcon': {
      color: '#6B7C87',
    },
  },
  filterButtonActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#E0EAFF',
    color: '#001C57',
    fontWeight: 600,
    '& .MuiButton-endIcon': {
      color: '#001C57',
    },
    '&:hover': {
      backgroundColor: '#D4E2FF',
      borderColor: theme.palette.primary.main,
    },
  },
  filterMenuPaper: {
    marginTop: theme.spacing(0.5),
    minWidth: 220,
    maxWidth: 320,
  },
  filterMenuContent: {
    padding: theme.spacing(1.5, 2),
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  filterForm: {
    margin: 0,
    padding: 0,
    border: 0,
    minWidth: 0,
  },
  filterLegend: {
    display: 'block',
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#6B7C87',
    padding: 0,
    margin: 0,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  filterRadioGroup: {
    padding: 0,
    margin: 0,
  },
  filterRadioLabel: {
    margin: 0,
    width: '100%',
    minHeight: 40,
    marginLeft: 0,
    marginRight: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      lineHeight: '20px',
      color: '#172024',
    },
    '& .MuiRadio-root': {
      color: '#40484C',
      padding: theme.spacing(1),
    },
    '& .MuiRadio-colorPrimary.Mui-checked': {
      color: '#0053FF',
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  searchGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: '1 1 0',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      width: '100%',
    },
  },
  searchField: {
    flex: '1 1 0',
    minWidth: 0,
    maxWidth: 360,
    width: '100%',
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0,
    marginLeft: 'auto',
  },
  tableContent: {
    padding: theme.spacing(0, 1, 1),
  },
  listEmptyStateContent: {
    padding: theme.spacing(4, 2),
  },
}))

const isInAgeGroup = ({ isMinor }, desiredGroup) => {
  if (desiredGroup === 'all') return true
  if (isMinor && desiredGroup === 'junior') return true
  if (!isMinor && desiredGroup === 'adult') return true
  return false
}

const hasCoach = ({ coaches }, desiredCoach) => {
  if (desiredCoach === 'all') return true
  return (coaches || []).some(
    coach => String(coach.id) === String(desiredCoach),
  )
}

const hasLocation = ({ facilities }, desiredLocation) => {
  if (desiredLocation === 'all') return true
  return (facilities || []).some(
    facility => String(facility.id) === String(desiredLocation),
  )
}

const nameMatchesSearchToken = ({ firstName, lastName }, token) => {
  const fullName = `${firstName} ${lastName}`.toLowerCase().trim()
  const term = token.toLowerCase().trim()
  return fullName.includes(term)
}

const uniqueById = items =>
  items.filter(
    (item, index, self) =>
      item?.id && index === self.findIndex(other => other.id === item.id),
  )

export default function StudentListView({ students }) {
  const classes = useStyles()
  const isTablet = useIsTablet()
  const searchableItems = useMemo(() => parseStudents(students), [students])
  const [filteredStudents, setFilteredStudents] = useState(searchableItems)
  const [searchTerm, setSearchTerm] = useState('')
  const [ageGroup, setAgeGroup] = useState('all')
  const [selectedCoach, setSelectedCoach] = useState('all')
  const [selectedLocation, setSelectedLocation] = useState('all')
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [page, setPage] = useState(0)
  const rowsPerPage = 25

  const uniqueCoaches = useMemo(
    () => uniqueById(searchableItems.flatMap(item => item.coaches || [])),
    [searchableItems],
  )
  const uniqueFacilities = useMemo(
    () => uniqueById(searchableItems.flatMap(item => item.facilities || [])),
    [searchableItems],
  )

  useEffect(() => {
    setFilteredStudents(
      searchableItems.filter(
        item =>
          nameMatchesSearchToken(item, searchTerm) &&
          isInAgeGroup(item, ageGroup) &&
          hasCoach(item, selectedCoach) &&
          hasLocation(item, selectedLocation),
      ),
    )
    setPage(0)
  }, [searchTerm, searchableItems, ageGroup, selectedCoach, selectedLocation])

  const filterMenuOpen = Boolean(filterAnchorEl)
  const activeAgeGroupLabel =
    AGE_FILTER_OPTIONS.find(option => option.value === ageGroup)?.label ||
    'All Ages'
  const selectedCoachName = uniqueCoaches.find(
    coach => String(coach.id) === String(selectedCoach),
  )?.name
  const selectedLocationName = uniqueFacilities.find(
    facility => String(facility.id) === String(selectedLocation),
  )?.name
  const activeFilterLabels = [
    ageGroup !== 'all' ? activeAgeGroupLabel : null,
    selectedCoach !== 'all' ? selectedCoachName : null,
    selectedLocation !== 'all' ? selectedLocationName : null,
  ].filter(Boolean)
  const hasActiveFilters = activeFilterLabels.length > 0
  const filterButtonLabel =
    activeFilterLabels.length === 1 ? activeFilterLabels[0] : 'Filters'

  const columns = [
    {
      id: 'name',
      label: 'Name',
      align: 'left',
      width: isTablet ? '50%' : '24%',
      mobile: true,
      sortable: true,
      sortComparator: sortByFullName,
    },
    {
      id: 'ageGroup',
      label: 'Age Group',
      align: 'left',
      width: isTablet ? '30%' : '12%',
      mobile: true,
      sortable: true,
      sortComparator: sortByAgeGroup,
      inactiveSortIcon: SwapVertIcon,
    },
    {
      id: 'upcoming',
      label: 'Next Upcoming Session',
      align: 'left',
      width: '28%',
      mobile: false,
      sortable: true,
      sortComparator: sortByUpcoming,
      inactiveSortIcon: SwapVertIcon,
    },
    {
      id: 'coach',
      label: 'Coach',
      align: 'left',
      width: '24%',
      mobile: false,
      sortable: true,
      sortComparator: sortByCoach,
      inactiveSortIcon: SwapVertIcon,
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      width: isTablet ? '20%' : '12%',
      mobile: true,
      sortable: false,
    },
  ]

  const pageCount =
    Math.ceil((filteredStudents?.length || 0) / rowsPerPage) || 1

  const rows = filteredStudents.map(student => (
    <StudentItemView student={student} key={student.id} />
  ))

  return (
    <TableContainer className={classes.containerStyles}>
      <Box className={classes.tableShell}>
        <Box className={classes.toolbar}>
          <Box className={classes.searchGroup}>
            <SearchBar
              className={classes.searchField}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              placeholder="Search Students"
              data-testid="student-list-search"
              style={{ margin: 0 }}
            />
            <Button
              variant="outlined"
              color="default"
              className={`${classes.filterButton} ${
                hasActiveFilters ? classes.filterButtonActive : ''
              }`}
              aria-controls="student-filters-menu"
              aria-haspopup="true"
              aria-expanded={filterMenuOpen}
              aria-label={
                hasActiveFilters
                  ? `Student filters: ${activeFilterLabels.join(', ')} selected`
                  : 'Student filters'
              }
              onClick={event => setFilterAnchorEl(event.currentTarget)}
              endIcon={<ArrowDropDownIcon />}
            >
              {filterButtonLabel}
            </Button>
            <Popover
              id="student-filters-menu"
              open={filterMenuOpen}
              anchorEl={filterAnchorEl}
              onClose={() => setFilterAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                className: classes.filterMenuPaper,
              }}
            >
              <Box className={classes.filterMenuContent}>
                <FormControl
                  component="fieldset"
                  fullWidth
                  className={classes.filterForm}
                >
                  <FormLabel
                    component="legend"
                    className={classes.filterLegend}
                  >
                    Age Group
                  </FormLabel>
                  <RadioGroup
                    aria-label="Age group filter"
                    name="age-group-filter"
                    className={classes.filterRadioGroup}
                    value={ageGroup}
                    onChange={event => setAgeGroup(event.target.value)}
                  >
                    {AGE_FILTER_OPTIONS.map(option => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        className={classes.filterRadioLabel}
                        control={<Radio color="primary" size="small" />}
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                {uniqueCoaches.length > 0 && (
                  <FormControl
                    component="fieldset"
                    fullWidth
                    className={classes.filterForm}
                  >
                    <FormLabel
                      component="legend"
                      className={classes.filterLegend}
                    >
                      Coach
                    </FormLabel>
                    <RadioGroup
                      aria-label="Coach filter"
                      name="coach-filter"
                      className={classes.filterRadioGroup}
                      value={selectedCoach}
                      onChange={event => setSelectedCoach(event.target.value)}
                    >
                      <FormControlLabel
                        value="all"
                        className={classes.filterRadioLabel}
                        control={<Radio color="primary" size="small" />}
                        label="All Coaches"
                      />
                      {uniqueCoaches.map(coach => (
                        <FormControlLabel
                          key={coach.id}
                          value={coach.id}
                          className={classes.filterRadioLabel}
                          control={<Radio color="primary" size="small" />}
                          label={coach.name}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}
                {uniqueFacilities.length > 0 && (
                  <FormControl
                    component="fieldset"
                    fullWidth
                    className={classes.filterForm}
                  >
                    <FormLabel
                      component="legend"
                      className={classes.filterLegend}
                    >
                      Location
                    </FormLabel>
                    <RadioGroup
                      aria-label="Location filter"
                      name="location-filter"
                      className={classes.filterRadioGroup}
                      value={selectedLocation}
                      onChange={event =>
                        setSelectedLocation(event.target.value)
                      }
                    >
                      <FormControlLabel
                        value="all"
                        className={classes.filterRadioLabel}
                        control={<Radio color="primary" size="small" />}
                        label="All Locations"
                      />
                      {uniqueFacilities.map(facility => (
                        <FormControlLabel
                          key={facility.id}
                          value={facility.id}
                          className={classes.filterRadioLabel}
                          control={<Radio color="primary" size="small" />}
                          label={facility.name}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}
              </Box>
            </Popover>
          </Box>
          <Box className={classes.toolbarActions}>
            {filteredStudents.length > 0 && (
              <StudentsTablePagination
                page={page}
                pageCount={pageCount}
                rowsPerPage={rowsPerPage}
                totalCount={filteredStudents.length}
                onPageChange={setPage}
              />
            )}
          </Box>
        </Box>
        <Box className={classes.tableContent}>
          <EnhancedTable
            rows={rows}
            columns={columns}
            label="Students"
            numberOfRows={rowsPerPage}
            variant="list"
            rowHeight="56px"
            initialOrderBy="name"
            initialOrder="asc"
            page={page}
            onPageChange={setPage}
            showHeaderPagination={false}
            showPaginator={false}
            emptyState={() => (
              <Box className={classes.listEmptyStateContent}>
                <SectionEmptyState
                  Icon={PermContactCalendarOutlinedIcon}
                  title="Students"
                  caption={
                    searchableItems.length === 0
                      ? 'Your students will appear here.'
                      : 'No matching students.'
                  }
                  bordered={false}
                />
              </Box>
            )}
          />
        </Box>
      </Box>
    </TableContainer>
  )
}
